import { lazy } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { PUBLIC_POLICY_METADATA } from 'Pages/Legal/policyMetadata'
import RedirectAuthenticatedRoot from 'Components/RoutesWrapper/RedirectAuthenticatedRoot'

const Home = lazy(() => import('./Pages/Home/Home'))
const DataRequestPage = lazy(() => import('Pages/RequestData/RequestData'))

const Careers = lazy(() => import('Pages/Careers/Careers'))
const BlogPost = lazy(() => import('Pages/BlogPost/BlogPost'))
const AboutUs = lazy(() => import('Pages/AboutUs/AboutUs'))
const ContactUs = lazy(() => import('./Pages/ContactUs/ContactUs'))
const FAQ = lazy(() => import('Pages/FAQ/FAQ'))
const KailSupportPage = lazy(() => import('Pages/Support/KailSupportPage'))
const EditUserDetails = lazy(
	() => import('Components/EditUserDetails/EditUserDetails'),
)
const MyNetwork = lazy(() => import('Pages/MyNetwork/MyNetwork'))
const NewMyListsPage = lazy(() => import('Components/Dashboard/DashboardLists'))
const OtherLists = lazy(() => import('./Pages/OtherLists/OtherLists'))
const UserProfile = lazy(() => import('Pages/UserProfile/UserProfile'))
const Register = lazy(() => import('./Pages/Register/Register'))

const SearchResults = lazy(() => import('./Pages/SearchResults/SearchResults'))
const PoliciesPage = lazy(() => import('Pages/Legal/PoliciesPage'))
const SharePost = lazy(() => import('Pages/SharePost/SharePost'))
const StaticPageRender = lazy(() => import('./Pages/StaticPages/StaticPages'))
const ViewList = lazy(() => import('./Pages/ViewList/ViewList'))
const ConfirmConnection = lazy(
	() => import('Pages/ConfirmConnection/ConfirmConnection'),
)
const SubscribeActivate = lazy(
	() => import('Pages/SubscribeActivate/SubscribeActivate'),
)
const Blogs = lazy(() => import('Pages/Blogs/Blogs'))
const AcceptShare = lazy(() => import('Pages/AcceptShare/AcceptShare'))
const UnsubscribeWeeklyUpdates = lazy(
	() => import('Pages/UnsubscribeWeeklyUpdates/UnsubscribeWeeklyUpdates'),
)
const UnsubscribeNewsletter = lazy(
	() => import('Pages/UnsubscribeNewsletter/UnsubscribeNewsletter'),
)
const BusinessProfile = lazy(
	() => import('Pages/BusinessProfile/BusinessProfile'),
)
const Playground = lazy(() => import('Pages/Playground/Playground'))
const Games = lazy(() => import('Pages/Games/Games'))
const GamesResults = lazy(() => import('Pages/GamesResults/GamesResults'))
const GamesSharedResult = lazy(() =>
	import('Pages/GamesSharedResult/GamesSharedResult'),
)
const Subscriptions = lazy(() => import('Pages/Subscriptions/Subscriptions'))
const Settings = lazy(() => import('Pages/Settings/Settings'))
const PasswordCallback = lazy(() => import('Pages/Settings/PasswordCallback'))

const Notifications = lazy(() => import('Pages/NotificationsPage'))

const MatchGame = lazy(() => import('Pages/Games/MatchGame/MatchGame'))
const MultipleChoiceGame = lazy(
	() => import('Pages/Games/MultipleChoiceGame/MultipleChoiceGame'),
)
const ScrambledKeywords = lazy(
	() => import('Pages/Games/ScrambledKeywords/ScrambledKeywords'),
)

const ChallengeHub = lazy(() => import('Pages/ChallengeHub/ChallengesHubPage'))
const ChallengeDetailsPage = lazy(
	() => import('Pages/ChallengeHub/ChallengeDetailsPage'),
)
const PaymentResultPage = lazy(
	() => import('Components/PaymentResult/PaymentResultPage'),
)

const LegacyProfileRedirect = () => {
	const { profileIdentifier = '' } = useParams()
	const location = useLocation()
	const targetProfile = encodeURIComponent(profileIdentifier)

	return (
		<Navigate
			to={`/my-network/${targetProfile}${location.search}${location.hash}`}
			replace
		/>
	)
}

export const protectedRoutes = [
	{
		path: '/register',
		name: 'Register',
		component: <Register />,
		sidebar: false,
	},
	{
		path: '/dashboard',
		name: 'Dashboard',
		component: (
			<Navigate
				to='/lists'
				replace
			/>
		),
		sidebar: true,
	},
	{
		path: '/lists',
		name: 'Lists',
		component: <NewMyListsPage />,
		sidebar: true,
		subRoutes: [
			{
				path: ':username',
				component: <OtherLists />,
				name: 'User List',
			},
		],
	},
	{
		path: '/edit-profile',
		name: 'Edit Profile',
		component: (
			<Navigate
				to='/profile/edit'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/profile',
		name: 'Profile',
		component: <UserProfile />,
		sidebar: false,
	},
	{
		path: '/profile/edit',
		name: 'Edit Profile Details',
		component: <EditUserDetails />,
		sidebar: false,
	},
	{
		path: '/profile/:profileIdentifier',
		name: 'Legacy Profile Redirect',
		component: <LegacyProfileRedirect />,
		sidebar: false,
	},
	{
		path: '/user-details',
		name: 'Legacy User Details',
		component: (
			<Navigate
				to='/profile/edit'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/edit-profile/change-password',
		name: 'Change Password',
		component: (
			<Navigate
				to='/profile/edit'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/edit-profile/user-details',
		name: 'Legacy User Details',
		component: (
			<Navigate
				to='/profile/edit'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/search-results',
		name: 'Search',
		component: <SearchResults />,
		sidebar: true,
	},
	{
		path: '/user-profile',
		name: 'Profile',
		component: (
			<Navigate
				to='/profile'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/my-network',
		name: 'My Network',
		component: <MyNetwork />,
		sidebar: true,
		subRoutes: [
			{
				path: ':username',
				component: <UserProfile />,
				name: 'Profile',
			},
		],
	},
	{
		path: '/playground',
		name: 'Playground',
		component: (
			<Navigate
				to='/global-leaderboard'
				replace
			/>
		),
		sidebar: true,
	},
	{
		path: '/global-leaderboard',
		name: 'Global Leaderboard',
		component: <Playground />,
		sidebar: true,
	},
	{
		path: '/games',
		name: 'Games',
		component: <Games />,
		sidebar: true,
	},
	{
		path: '/games/results',
		name: 'Game Results',
		component: <GamesResults />,
		sidebar: false,
	},
	{
		path: '/view-list/:subjectHref/:listID',
		name: 'View List',
		component: <ViewList />,
		sidebar: false,
	},
	{
		path: '/confirm-connection/:userID',
		name: 'Confirm Connection',
		component: <ConfirmConnection />,
		sidebar: false,
	},
	{
		path: '/accept-share/:token',
		name: 'Accept Share',
		component: <AcceptShare />,
		sidebar: false,
	},
	{
		path: '/lists/shared/:token',
		name: 'Shared List',
		component: <AcceptShare />,
		sidebar: false,
	},
	{
		path: '/settings',
		name: 'Settings',
		component: <Settings />,
		sidebar: false,
	},
	{
		path: '/payment-success',
		name: 'Payment Success',
		component: <PaymentResultPage status='success' />,
		sidebar: false,
	},
	{
		path: '/payment-failed',
		name: 'Payment Failed',
		component: <PaymentResultPage status='failure' />,
		sidebar: false,
	},
	{
		path: '/notifications',
		name: 'Notifications',
		component: <Notifications />,
		sidebar: true,
	},
	{
		path: '/challenge-hub',
		name: 'Challenge Hub',
		component: <ChallengeHub />,
		sidebar: false,
	},
	{
		path: '/challenge-hub/challenge-details/:id',
		name: 'Challenges Details',
		component: <ChallengeDetailsPage />,
		sidebar: false,
	},
	{
		path: '/request-data',
		name: 'Request Data',
		component: <DataRequestPage />,
		sidebar: false,
	},
]

export const onlyLoggedOutRoutes = [
	// {
	//   path: "/register",
	//   name: "Register",
	//   component: <Register />,
	// },
	// {
	//   path: "/activate/:userID",
	//   name: "Activate",
	//   component: <Activate />,
	//   sidebar: false,
	// },
	// {
	//   path: "/reset-password/:userID",
	//   name: "Reset Password",
	//   component: <ResetPassword />,
	// },
	// {
	//   path: "/auth/login",
	//   name: "Login",
	//   component: <Login />,
	// },

	{
		path: '/signup',
		name: 'Signup',
		component: (
			<Navigate
				to='/register'
				replace
			/>
		),
	},
	// Login route removed. Keycloak login page is used.
	{
		path: '/subscribe/:ID',
		name: 'Subscribe',
		component: <SubscribeActivate />,
	},
]

export const publicRoutes = [
	{
		path: '/',
		name: 'Home',
		component: (
			<RedirectAuthenticatedRoot>
				<Home />
			</RedirectAuthenticatedRoot>
		),
	},
	{
		path: '/settings/password-callback',
		name: 'Password Callback',
		component: <PasswordCallback />,
		sidebar: false,
	},
	{
		path: '/faq',
		name: 'FAQ',
		component: <FAQ />,
		sidebar: false,
	},
	{
		path: '/FAQs',
		name: 'FAQs',
		component: <FAQ />,
		sidebar: false,
	},
	{
		path: '/contact-us',
		name: 'Contact Us',
		component: <ContactUs />,
		sidebar: false,
	},
	{
		path: '/contact',
		name: 'Contact Us',
		component: <ContactUs />,
		sidebar: false,
	},
	{
		path: '/support/kail',
		name: 'KAIL Support',
		component: <KailSupportPage />,
		sidebar: false,
	},
	{
		path: '/blogs',
		name: 'Blogs',
		component: <Blogs />,
		sidebar: true,
	},
	{
		path: '/careers',
		name: 'Careers',
		component: <Careers />,
		sidebar: false,
	},
	{
		path: '/about-us',
		name: 'About-Us',
		component: <AboutUs />,
		sidebar: false,
	},
	{
		path: '/posts/:postId',
		name: 'Posts',
		component: <SharePost />,
		sidebar: false,
	},
	{
		path: '/games/shared-results/:token',
		name: 'Shared Game Result',
		component: <GamesSharedResult />,
		sidebar: false,
	},
	{
		path: '/results/shared/:token',
		name: 'Shared Game Result',
		component: <GamesSharedResult />,
		sidebar: false,
	},
	{
		path: '/games/match-game',
		name: 'Match Game',
		component: <MatchGame />,
		sidebar: true,
	},
	{
		path: '/games/multiple-choice',
		name: 'Multiple Choice Game',
		component: <MultipleChoiceGame />,
		sidebar: false,
	},
	{
		path: '/games/scrambled-keywords',
		name: 'Scrambled Keywords',
		component: <ScrambledKeywords />,
		sidebar: true,
	},
	{
		path: '/subscriptions',
		name: 'Subscriptions Plan',
		component: <Subscriptions />,
		sidebar: false,
	},
	{
		path: '/policies',
		name: 'Policies',
		component: (
			<Navigate
				to='/privacy-policy'
				replace
			/>
		),
		sidebar: false,
	},
	{
		path: '/policies/ai-usage',
		name: 'AI Usage and Data Policy',
		component: <PoliciesPage policyId='ai-usage-data-policy' />,
		sidebar: false,
	},
	{
		path: '/policies/privacy',
		name: 'Privacy Policy',
		component: <PoliciesPage policyId='privacy-policy' />,
		sidebar: false,
	},
	{
		path: '/policies/safeguarding',
		name: 'Safeguarding Policy',
		component: <PoliciesPage policyId='child-safety-minor-access-policy' />,
		sidebar: false,
	},
	...PUBLIC_POLICY_METADATA.map((policy) => ({
		path: policy.path,
		name: policy.title,
		component: <PoliciesPage policyId={policy.id} />,
		sidebar: false,
	})),
	{
		path: '/blog/:slug',
		name: 'Blog Post',
		component: <BlogPost />,
		sidebar: false,
	},
	{
		path: '/:pageHref',
		name: 'Demo Subject',
		component: <StaticPageRender />,
		sidebar: false,
	},
	{
		path: '/unsubscribe-weekly-updates/:ID',
		name: 'Unsubscribe Weekly updates',
		component: <UnsubscribeWeeklyUpdates />,
		sidebar: false,
	},
	{
		path: '/unsubscribe-newsletter/:ID',
		name: 'Unsubscribe Newsletter',
		component: <UnsubscribeNewsletter />,
		sidebar: false,
	},
	{
		path: '/business-profile',
		name: 'Business Profile',
		component: <BusinessProfile />,
		sidebar: false,
	},
]
